import { EadNode, EadNodeParentView } from 'types';
import { EadSimple } from '../ead-context';
import _, { get } from 'lodash';

export const isSame = (n1?: EadSimple, n2?: EadSimple) => {
  if (!n1 || !n2) return false;
  if (n1.id === n2.id) return true;
  const bid1 = get(n1, 'baseIdentifier');
  const bid2 = get(n2, 'baseIdentifier');
  if (bid1 && bid2 && bid1 === bid2) return true;
  return false;
};

export const addNewChildren = (
  flatTree: EadSimple[],
  newChildren: EadSimple[]
) => {
  const newTree = [...flatTree];
  newChildren.forEach((c) => {
    const entryIndex = newTree.findIndex((x) => isSame(x, c));
    if (entryIndex > -1) {
      newTree.splice(
        entryIndex + 1,
        0,
        ...c.children.map((child) => ({
          ...child,
          nlevel: newTree[entryIndex].nlevel + 1,
        }))
      );
    }
  });
  return newTree;
};

export const getAllNodeChildren = (flatTree: EadSimple[], node: EadSimple) => {
  let nodeIndex = flatTree.findIndex((n) => isSame(n, node));
  if (nodeIndex < 0) return { children: [], startIndex: -1 };
  const startIndex = nodeIndex;
  const parentLevel = flatTree[nodeIndex].nlevel;
  const children = [];
  while (nodeIndex <= flatTree.length) {
    nodeIndex++;
    if (!flatTree[nodeIndex]) break;
    if (flatTree[nodeIndex]?.nlevel <= parentLevel) break;
    children.push(flatTree[nodeIndex]);
  }
  return { children, startIndex };
};

export const mergeTrees = (tree1: EadSimple[], newTree: EadSimple[]) => {
  if (!tree1 || !newTree) return [];

  const newLeaf = _.maxBy(newTree, (n) => n.nlevel) as EadSimple;
  if (tree1.findIndex((n) => isSame(n, newLeaf)) !== -1) {
    return tree1;
  }
  const newLeafFirstIndex = newTree.findIndex((n) => isSame(n, newLeaf));
  const lastIndex = newTree.findIndex(
    (n, i) =>
      n.nlevel >= newLeaf.nlevel &&
      (newTree[i + 1] === undefined || newTree[i + 1]?.nlevel < newLeaf.nlevel)
  );

  const parentIndex = newLeafFirstIndex - 1;
  const entryIndex = tree1.findIndex((n) => isSame(n, newTree[parentIndex]));

  const mergedTree = [
    ...tree1.slice(0, entryIndex),
    ...newTree.slice(parentIndex, lastIndex + 1),
    ...tree1.slice(entryIndex + 1),
  ];

  return mergedTree;
};

export const createFlatEadTree = (ead?: EadSimple) => {
  if (!ead) return [];
  const stack: EadSimple[] = [];
  const flatTree: EadSimple[] = [];
  let node: EadSimple = { ...ead };

  while (node.parent) {
    stack.push({ ...node });
    node = { ...node.parent } as EadSimple;
  }
  stack.push({ ...node, level: 'aid', levelLabel: 'Pomůcka' }); // push root

  // assign nested level
  for (let i = 0; i < stack.length; i++) {
    stack[i].nlevel = stack.length - (i + 1);
  }

  let childrenStack: EadSimple[] = [];
  let root = stack.pop() as EadSimple;

  while (stack.length > 0 || (root && !isSame(root, ead))) {
    flatTree.push({ ...root });
    if (root.children) {
      const rootLevel = root.nlevel;
      const children = [...root.children].map((c) => ({
        ...c,
        nlevel: rootLevel + 1,
      }));
      childrenStack.push(...children.reverse());
    }

    if (stack.length > 0) {
      root = stack.pop() as EadSimple;
    }
    if (root) {
      let child = childrenStack.pop();
      while (!isSame(root, child) && childrenStack.length > 0) {
        if (child) {
          flatTree.push({ ...child });
        }
        child = childrenStack.pop();
      }
    } else {
      // TODO: tento else mozno zbytocny

      flatTree.push(...childrenStack);
    }
  }
  const children = [...root.children].map((c) => ({
    ...c,
    nlevel: root.nlevel + 1,
  }));

  flatTree.push(root, ...children);
  flatTree.push(...childrenStack.reverse());
  return flatTree;
};

export const getEadRoot = (ead: EadNode) => {
  const stack: EadNodeParentView[] = [];
  let node: EadNodeParentView = (ead as unknown) as EadNodeParentView;
  while (node.parent) {
    stack.push({ ...node });
    node = { ...node.parent };
  }
  if (!node) {
    return undefined;
  }

  const rootArchivalAidId =
    node?.archivalAidMetadata?.baseIdentifier ?? node?.archivalAidMetadata?.id;

  if (!rootArchivalAidId) {
    return undefined;
  }

  return {
    rootArchivalAidId,
    rootArchivalCollectionId: node?.archivalAidMetadata?.archivalCollection?.id,
  };
};
